import React, {useEffect, useState} from "react";
import '../assets/styles/bluetooth_component_final.css';
import fingerPrintLogo from "../assets/images/icons/reconocimiento dactilar_negro.jpg";
import {useNavigate} from "react-router-dom";

const FingerprintScanner = () => {
    const [device, setDevice] = useState<BluetoothDevice | null>(null);
    const [status, setStatus] = useState("❌ Lector de huella desconectado");
    const [writeCharacteristic, setWriteCharacteristic] = useState<BluetoothRemoteGATTCharacteristic | null>(null);
    const [responseCharacteristic, setResponseCharacteristic] = useState<BluetoothRemoteGATTCharacteristic | null>(null);
    const [base64Png, setBase64Png] = useState<string | undefined>(undefined);
    const [finger1, setFinger1] = useState<string | null>(null);
    const [finger2, setFinger2] = useState<string | null>(null);
    const [finger3, setFinger3] = useState<string | null>(null);
    const [captureStep, setCaptureStep] = useState<1 | 2>(1);
    const [isCapturing, setIsCapturing] = useState(false);
    const [isConnected, setIsConnected] = useState(false);
    const navigate = useNavigate();

    // Service UUID for the fingerprint scanner
    const SERVICE_UUID = "0000fff0-0000-1000-8000-00805f9b34fb";

    // Connect to the device
    const connectToDevice = async () => {
        try {
            setStatus("Buscando dispositivo...");

            const selectedDevice = await navigator.bluetooth.requestDevice({
                acceptAllDevices: true,
                optionalServices: [SERVICE_UUID],
            });

            const server = await selectedDevice.gatt?.connect() as BluetoothRemoteGATTServer | undefined;

            if (!server) throw new Error("No se pudo conectar al servidor GATT");

            setStatus(`✅ Conectado con el lector de huella ${selectedDevice?.name || "Dispositivo desconocido"}`);

            setDevice(selectedDevice);

            // get the GATT server
            const service = await server?.getPrimaryService("0000fff0-0000-1000-8000-00805f9b34fb");

            // get the characteristic for reading responses
            const response = await service?.getCharacteristic("0000fff1-0000-1000-8000-00805f9b34fb");

            // get the characteristic for writing commands
            const characteristic = await service?.getCharacteristic("0000fff2-0000-1000-8000-00805f9b34fb"); // usa el UUID correcto para escritura

            // save the characteristics in state
            setResponseCharacteristic(response);
            setWriteCharacteristic(characteristic);
            setIsConnected(true);

            console.log("Dispositivo conectado y característica lista:", selectedDevice);

        } catch (error) {
            console.error("Error al conectar con el dispositivo:", error);
            setStatus("Error al conectar con el dispositivo.");
            setIsConnected(false);
        }
    };

    const disconnectFromDevice = async () => {
        try {
            if (device && device.gatt) {
                await (device.gatt as any).disconnect();
                setDevice(null);
                setStatus("Dispositivo desconectado");
                setIsConnected(false);
                setCaptureStep(1)
                console.log("Dispositivo desconectado");
            } else {
                setStatus("No hay dispositivo conectado");
            }
        } catch (error) {
            console.error("Error al desconectar el dispositivo:", error);
            setStatus("Error al desconectar el dispositivo");
        }
    };

    useEffect(() => {
        if (!device) return;

        const handleDisconnect = () => {
            console.warn("🔌 Dispositivo desconectado automáticamente");
            setStatus("🔌 Dispositivo desconectado");
            setIsConnected(false);
            setDevice(null);
        };

        // 👇 Type cast necesario
        (device as unknown as EventTarget).addEventListener("gattserverdisconnected", handleDisconnect);

        return () => {
            (device as unknown as EventTarget).removeEventListener("gattserverdisconnected", handleDisconnect);
        };
    }, [device]);

    /**
     * Finds and returns the first writable Bluetooth characteristic UUID within a specific service
     * of the connected device.
     *
     * Purpose:
     * 1. Scans the specified service (defined by SERVICE_UUID) for characteristics
     * 2. Identifies the first characteristic that has write permissions (either 'write'
     *    or 'writeWithoutResponse' properties)
     * 3. Returns its UUID if found, or null otherwise
     *
     * Note: Currently uses a non-standard getCharacteristics() implementation which may
     * not work in all environments. For production use, prefer getCharacteristic()
     * with known UUIDs.
     */
    const getAvailableServices = async (responseCharacteristic : BluetoothRemoteGATTCharacteristic) => {
        if (!device) {
            console.error("No hay un dispositivo conectado.");
            return;
        }

        try {
            if (!device.gatt) {
                console.error("El dispositivo no soporta GATT.");
                return;
            }

            const server = await device?.gatt?.connect() as BluetoothRemoteGATTServer | undefined;
            const service = await server?.getPrimaryService(SERVICE_UUID);

            console.log("📋 Propiedades de responseChar:", responseCharacteristic?.properties);

            if (!service) {
                console.error("Servicio no encontrado.");
                return;
            }

            const characteristics = await (service as any).getCharacteristics();
            console.log("Características disponibles:", characteristics.map((c: any) => c.uuid));

            for (const characteristic of characteristics) {
                const properties = characteristic.properties;
                console.log(`Propiedades de ${characteristic.uuid}:`, properties);

                if (properties.write || properties.writeWithoutResponse) {
                    console.log("Característica con escritura encontrada:", characteristic.uuid);

                    return characteristic.uuid; // Returns the first writable feature
                }
            }

            console.error("No se encontraron características que permitan escritura.");
            return null;
        } catch (error) {
            console.error("Error al obtener los servicios:", error);
        }
    };

    // This method is only useful if there is information about the saved ID templates
    const clearDeviceBuffer = async (responseCharacteristic : BluetoothRemoteGATTCharacteristic, writeCharacteristic : BluetoothRemoteGATTCharacteristic)  => {
        if (!device) {
            alert("No hay dispositivo conectado.");
            return false;
        }

        try {

            const writeChar = writeCharacteristic;
            const responseChar = responseCharacteristic;

            if (!writeChar || !responseChar) {
                throw new Error("No se pudieron obtener las características del dispositivo.");
            }


            // ✅ Step 1: send CMD_PASSWORD to authenticate id the device requires it
            const passwordResp = await sendCommandAndGetResponse(writeChar, responseChar, buildPasswordCommand());

            const passwordStatus = passwordResp[7];

            if (passwordStatus !== 0x00) {
                console.error("❌ Error de autenticación con CMD_PASSWORD. Código:", passwordStatus);
                return false;
            }

            console.log("🔓 Autenticación exitosa con CMD_PASSWORD");

            // ✅ Step 2: send CMD_CLEARID = 0x05 to clear the buffer
            const clearCmd = buildClearBufferCommand();
            const response = await sendCommandAndGetResponse(writeChar, responseChar, clearCmd);
            const status = response[7];

            if (status === 0x00) {
                console.log("🧹 Buffer limpiado correctamente");
            } else if (status === 0x01) {
                console.warn("ℹ️ No se pudo limpiar: el buffer puede estar vacío o el ID no existe.");
            } else {
                console.warn("⚠️ Fallo al limpiar buffer. Código de estado:", status);
            }

        } catch (error) {
            console.error("❌ Error al limpiar el buffer del dispositivo:", error);
            return false;
        }
    };

    const sendCommandAndGetResponse = async (
        writeChar: BluetoothRemoteGATTCharacteristic,
        responseChar: BluetoothRemoteGATTCharacteristic,
        command: Uint8Array,
        timeoutMs = 10000
    ): Promise<Uint8Array> => {
        return new Promise(async (resolve, reject) => {
            let timeoutHandle: ReturnType<typeof setTimeout>;

            const onResponse = (event: Event) => {
                const value = (event.target as unknown as BluetoothRemoteGATTCharacteristic).value;
                if (!value) {
                    reject("❌ Respuesta vacía del dispositivo");
                    return;
                }

                const response = new Uint8Array(value.buffer);
                clearTimeout(timeoutHandle);
                responseChar.removeEventListener("characteristicvaluechanged", onResponse);
                resolve(response);
            };

            try {
                console.log("🔔 Activando notificaciones...");
                responseChar.addEventListener("characteristicvaluechanged", onResponse);
                await responseChar.startNotifications();
                console.log("✅ Notificaciones activadas");

                await new Promise(r => setTimeout(r, 400)); // Delay opcional antes de escribir

                console.log("➡️ Enviando comando:", command);
                await writeChar.writeValue(command);

                // ⏱ Timeout por si no responde
                timeoutHandle = setTimeout(() => {
                    responseChar.removeEventListener("characteristicvaluechanged", onResponse);
                    reject("⏰ Timeout: no se recibió respuesta del dispositivo");
                }, timeoutMs);

            } catch (error) {
                responseChar.removeEventListener("characteristicvaluechanged", onResponse);
                reject("❌ Error en comunicación GATT: " + error);
            }
        });
    };

    // This method is for the image and the template and for the match sendCommandAndGetResponse is used
    const sendCommandAndGetResponseMultiPacket = async (
        writeChar: BluetoothRemoteGATTCharacteristic,
        responseChar: BluetoothRemoteGATTCharacteristic,
        command: Uint8Array,
        expectedTotalSize: number,
        fragmentTimeout = 2000 // time to wait for each fragment before considering it a timeout
    ): Promise<Uint8Array> => {
        return new Promise(async (resolve, reject) => {
            let bufferList: Uint8Array[] = [];
            let totalReceived = 0;
            let timeoutHandle: NodeJS.Timeout;
            let receivedFragments = 0;
            let lastFragmentSize = 0;

            const clearAll = () => {
                responseChar.removeEventListener("characteristicvaluechanged", onResponse);
                clearTimeout(timeoutHandle);
            };

            const ensureConnection = async () => {
                if (!device) {
                    const selectedDevice = await navigator.bluetooth.requestDevice({
                    acceptAllDevices: true,
                    optionalServices: [SERVICE_UUID],
                });
                    console.log("Reconectando dispositivo...");
                    await selectedDevice?.gatt?.connect();
                    await new Promise(resolve => setTimeout(resolve, 300));
                }
            };

            const onResponse = (event: Event) => {
                const value = (event.target as any).value;
                if (!value) return;

                const chunk = new Uint8Array(value.buffer);
                bufferList.push(chunk);
                totalReceived += chunk.length;
                receivedFragments++;
                lastFragmentSize = chunk.length;

                // Mostrar solo los primeros 2 fragmentos y los últimos 2
                if (receivedFragments <= 2 || totalReceived >= expectedTotalSize - lastFragmentSize) {
                    console.log(`📨 Fragmento recibido: ${chunk.length} bytes`);
                    console.log(`📦 Acumulado: ${totalReceived}/${expectedTotalSize} bytes`);
                }

                if (totalReceived >= expectedTotalSize) {
                    clearAll();
                    const fullResponse = new Uint8Array(totalReceived);
                    let offset = 0;
                    for (const part of bufferList) {
                        fullResponse.set(part, offset);
                        offset += part.length;
                    }
                    console.log("✅ Transferencia completa");
                    resolve(fullResponse);
                }

                // Reset timeout for next fragment
                clearTimeout(timeoutHandle);
                timeoutHandle = setTimeout(() => {
                    clearAll();
                    console.warn("⚠️ Timeout alcanzado. Enviando lo que se recibió.");
                    console.log(fragmentTimeout);
                    const partialResponse = new Uint8Array(totalReceived);
                    let offset = 0;
                    for (const part of bufferList) {
                        partialResponse.set(part, offset);
                        offset += part.length;
                    }
                    resolve(partialResponse);
                }, fragmentTimeout);
            };

            try {
                await ensureConnection();
                await new Promise(r => setTimeout(r, 200));

                console.log("🔔 Activando notificaciones...");
                responseChar.addEventListener("characteristicvaluechanged", onResponse);
                await responseChar.startNotifications();

                await new Promise(r => setTimeout(r, 100));

                console.log("➡️ Enviando comando:", command);
                await writeChar.writeValue(command);
            } catch (error) {
                clearAll();
                reject("❌ Error al enviar comando o configurar notificaciones: " + error);
            }
        });
    };

// Luego llamas a esta función en lugar de startNotifications directamente

    // Builds a command to get the character from the device
    const buildGetCharCommand = (bufferId: number): Uint8Array => {
        const HEAD = [0x46, 0x54];
        const RESERVED = [0x00, 0x00];
        const CMD = 0x31;
        const DATA = [bufferId]; // por ejemplo: 0x01 para buffer 1
        const SIZE = [0x01, 0x00]; // 1 byte de data

        const checksumValue = CMD + SIZE[0] + SIZE[1] + DATA[0];
        const checksum = [(checksumValue >> 8) & 0xFF, checksumValue & 0xFF];

        return new Uint8Array([
            ...HEAD,
            ...RESERVED,
            CMD,
            ...SIZE,
            ...DATA,
            ...checksum
        ]);
    };

    const buildSimpleCommand = (CMD: number): Uint8Array => {
        const HEAD = [0x46, 0x54]; // 'FT'
        const RESERVED = [0x00, 0x00];
        const SIZE = [0x00, 0x00]; // sin datos
        const checksumVal = CMD + SIZE[0] + SIZE[1];
        const checksum = [(checksumVal >> 8) & 0xff, checksumVal & 0xff];

        return new Uint8Array([
            ...HEAD,
            ...RESERVED,
            CMD,
            ...SIZE,
            ...checksum
        ]);
    };

    const buildClearBufferCommand = (): Uint8Array => {
        const HEADER = [0x46, 0x54];          // "FT"
        const RESERVED = [0x00, 0x00];
        const CMD = 0x06;                     // CMD_CLEARID
        const SIZE = [0x00, 0x00];            // Sin datos
        const CHECKSUM = [(CMD >> 8) & 0xFF, CMD & 0xFF];  // Igual al CMD porque no hay data

        return new Uint8Array([
            ...HEADER,
            ...RESERVED,
            CMD,
            ...SIZE,
            ...CHECKSUM
        ]);
    };

    // builds a command to generate an image from the device
    const buildGenImgCommand = (): Uint8Array => {
        const HEAD = [0x46, 0x54]; // FT
        const RESERVED = [0x00, 0x00];
        const CMD = 0x30;
        const SIZE = [0x00, 0x00]; // data size = 0

        const checksum = [(CMD + SIZE[0] + SIZE[1]) >> 8, (CMD + SIZE[0] + SIZE[1]) & 0xff];

        return new Uint8Array([
            ...HEAD,
            ...RESERVED,
            CMD,
            ...SIZE,
            ...checksum
        ]);
    };

    // Builds a command to match fingerprints in the device
    const buildMatchCommand = (): Uint8Array => {
        const HEAD = [0x46, 0x54];
        const RESERVED = [0x00, 0x00];
        const CMD = 0x09; // CMD_MATCH
        const SIZE = [0x00, 0x00];
        const checksum = [(CMD + SIZE[0] + SIZE[1]) >> 8, (CMD + SIZE[0] + SIZE[1]) & 0xFF];

        return new Uint8Array([
            ...HEAD,
            ...RESERVED,
            CMD,
            ...SIZE,
            ...checksum
        ]);
    };

    // Builds a command to clear the device buffer
    const buildPasswordCommand = (): Uint8Array => {
        const HEAD     = [0x46, 0x54];           // "FT"
        const RESERVED = [0x00, 0x00];           // Always zero
        const CMD                = 0x01;                   // CMD_PASSWORD
        const SIZE     = [0x04, 0x00];           // 4 bytes de DATA
        const DATA     = [0x00, 0x00, 0x00, 0x00]; // Password 4 bytes (0x00, 0x00, 0x00, 0x00)

        // Checksum = CMD + SIZE_LO + SIZE_HI + sum(DATA)
        const sum = CMD + SIZE[0] + SIZE[1] + DATA.reduce((a, b) => a + b, 0);
        const CHECKSUM = [(sum >> 8) & 0xff, sum & 0xff]; // [0x00, 0x05]

        const packet = new Uint8Array([
            ...HEAD,
            ...RESERVED,
            CMD,
            ...SIZE,
            ...DATA,
            ...CHECKSUM
        ]);

        console.log("🔧 buildPasswordCommand -> length:", packet.length, packet);
        return packet;
    };

    // Shared fingerprint capture logic
    const captureFingerprint = async (
        responseChar: BluetoothRemoteGATTCharacteristic,
        writeChar: BluetoothRemoteGATTCharacteristic,
        bufferNumber: 1 | 2 | 3
    ): Promise<Uint8Array | null> => {
        if (!device) {
            alert("No hay dispositivo 'Lector de Huella' conectado");
            return null;
        }

        const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

        try {
            setIsCapturing(true);
            setStatus(`📸 Capturando huella ${bufferNumber}...`);

            // Prepare buffer
            const cmdEnroll = buildSimpleCommand(0x07);
         //   await sendCommandAndGetResponse(writeChar, responseChar, cmdEnroll);
         //   await delay(200);

            // Capture fingerprint
            const cmdCapture = buildSimpleCommand(0x08);
          //  await sendCommandAndGetResponse(writeChar, responseChar, cmdCapture);
          //  await delay(200);

            // Verify template in buffer
            // await checkTemplateInBuffer(bufferNumber, writeChar, responseChar);
            // await delay(300);
            // setStatus(`✅ Huella ${bufferNumber} capturada correctamente`);

            return await getFingerprintImage(writeChar, responseChar);

        } catch (error) {
            console.error(`❌ Error capturando huella ${bufferNumber}:`, error);
            setStatus(`Error capturando huella ${bufferNumber}`);
            return null;
        } finally {
            setIsCapturing(false);
        }
    };

    // Image capture logic
    const getFingerprintImage = async (
        writeChar: BluetoothRemoteGATTCharacteristic,
        responseChar: BluetoothRemoteGATTCharacteristic
    ): Promise<Uint8Array | null> => {
        try {
            setStatus("🖼️ Capturando imagen...");

            const cmdGenImg = buildGenImgCommand();
            const respGenImg = await sendCommandAndGetResponseMultiPacket(
                writeChar,
                responseChar,
                cmdGenImg,
                36864,
                15000
            );

            if (respGenImg.length < 20) {
                console.log("✅ Imagen capturada correctamente (paquete corto)");
                return respGenImg; // Return the short response
            } else {
                console.log("📸 Imagen recibida completamente (bloques):", respGenImg.length, "bytes");
                return respGenImg.slice(0, 36864); // Return raw image data
            }
        } catch (error) {
            console.error("❌ Error capturando imagen:", error);
            return null;
        }
    };

    // Save fingerprint to state and handle image downloads
    const saveFingerprint = (imageData: Uint8Array, bufferNumber: 1 | 2 | 3) => {
        try {
            // Image base64, PNG encoded
            const base64 = convertToBase64(imageData);
            localStorage.setItem(`fingerprint${bufferNumber}`, base64);

            // Without header if we need to save it into the DB
            const base64Only = base64.split(",")[1];
            localStorage.setItem(`fingerPrintWithoutHeader${bufferNumber}`, base64Only);

            const fullWidth = 256;
            const realWidth = 128;
            const height = 144;
            const width = 128;

            const croppedImage = new Uint8Array(realWidth * height);

            for (let y = 0; y < height; y++) {
                for (let x = 0; x < realWidth; x++) {
                    // croppedImage[y * realWidth + x] = respGenImg[y * fullWidth + x];
                    // Si quieres la derecha en lugar de la izquierda:
                    croppedImage[y * realWidth + x] = imageData[y * fullWidth + (x + realWidth)];
                }
            }

            const base64Cropped = convertCroppedToBase64(croppedImage);

            // Save RAW image file (from original method)
            const blobRaw = new Blob([croppedImage], { type: "application/octet-stream" });
            const urlRaw = URL.createObjectURL(blobRaw);
            const aRaw = document.createElement("a");
            // En la parte RAW:
            // const aRaw = document.createElement("a");
            // aRaw.href = urlRaw;
            // aRaw.download = `fingerprint${bufferNumber}.raw`;
            // document.body.appendChild(aRaw);
            // aRaw.click();
            // document.body.removeChild(aRaw);
            // console.log(`✅ Imagen ${bufferNumber}.raw descargada`);

            // Save PNG image (from original method)
            const canvas = document.createElement("canvas");
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext("2d");

            if (!ctx) {
                console.error("❌ Error: no se pudo obtener el contexto 2D del canvas.");
                return;
            }

            const imageDataCtx = ctx.createImageData(width, height);
            const data = imageDataCtx.data;

            // Convert grayscale to RGBA
            for (let i = 0; i < croppedImage.length; i++) {
                const gray = croppedImage[i];
                data[i * 4] = gray;     // R
                data[i * 4 + 1] = gray; // G
                data[i * 4 + 2] = gray; // B
                data[i * 4 + 3] = 255;  // A
            }

            ctx.putImageData(imageDataCtx, 0, 0);

            // Export as PNG
            canvas.toBlob(blob => {
                if (blob) {
                    // En la parte PNG (dentro del canvas.toBlob):
                    // const aPng = document.createElement("a");
                    // aPng.href = urlPng;
                    // aPng.download = `fingerprint${bufferNumber}.png`;
                    // console.log(`✅ Imagen ${bufferNumber}.png descargada`);
                    // document.body.appendChild(aPng);
                    // aPng.click();
                    // document.body.removeChild(aPng);
                } else {
                    console.error("❌ No se pudo crear el blob PNG.");
                }
            }, "image/png");

            if (bufferNumber === 1) {
                setFinger1(base64Only);
                setBase64Png(base64Cropped);
            } else if (bufferNumber === 2) {
                setFinger2(base64Only);
                setBase64Png(base64Cropped);
            } else if (bufferNumber === 3) {
                setFinger3(base64Only);
                setBase64Png(base64Cropped);
            }

        } catch (error) {
            console.error(`❌ Error guardando huella ${bufferNumber}:`, error);
        }
    };

    // Nueva función para convertir imagen recortada a Base64
    const convertCroppedToBase64 = (croppedImage: Uint8Array): string => {
        const canvas = document.createElement("canvas");
        canvas.width = 128;  // Ancho recortado
        canvas.height = 144;
        const ctx = canvas.getContext("2d");

        if (!ctx) throw new Error("No se pudo obtener contexto 2D");

        const imageData = ctx.createImageData(128, 144);
        for (let i = 0; i < croppedImage.length; i++) {
            imageData.data[i * 4] = croppedImage[i];     // R
            imageData.data[i * 4 + 1] = croppedImage[i]; // G
            imageData.data[i * 4 + 2] = croppedImage[i]; // B
            imageData.data[i * 4 + 3] = 255;             // A
        }

        ctx.putImageData(imageData, 0, 0);
        return canvas.toDataURL("image/png");
    };

    // Convert raw image to Base64
    const convertToBase64 = (imageRaw: Uint8Array): string => {
        const canvas = document.createElement("canvas");
        canvas.width = 256;
        canvas.height = 144;
        const ctx = canvas.getContext("2d");

        if (!ctx) throw new Error("No se pudo obtener contexto 2D");

        const imageData = ctx.createImageData(256, 144);
        for (let i = 0; i < imageRaw.length; i++) {
            imageData.data[i * 4] = imageRaw[i];     // R
            imageData.data[i * 4 + 1] = imageRaw[i]; // G
            imageData.data[i * 4 + 2] = imageRaw[i]; // B
            imageData.data[i * 4 + 3] = 255;         // A
        }

        ctx.putImageData(imageData, 0, 0);
        return canvas.toDataURL("image/png");
    };

    // Main capture handlers
    const captureFingerprint01 = async (
        responseChar: BluetoothRemoteGATTCharacteristic,
        writeChar: BluetoothRemoteGATTCharacteristic
    ) => {
        const imageData = await captureFingerprint(responseChar, writeChar, 1);
        if (imageData) {
            saveFingerprint(imageData, 1);
            setCaptureStep(2);
            setStatus("✅  Huella 1 Capturada");
        }
    };

    const captureFingerprint02 = async (
        responseChar: BluetoothRemoteGATTCharacteristic,
        writeChar: BluetoothRemoteGATTCharacteristic
    ) => {
        const imageData = await captureFingerprint(responseChar, writeChar, 2);
        if (imageData) {
            saveFingerprint(imageData, 2);
            setCaptureStep(1);
            setStatus("✅  Huella 2 Capturada");
           // setStatus("🔍 Comparando huellas...");
          //  await compareFingerprints(responseChar, writeChar);
        }
    };

    const captureFingerprint03 = async (
        responseChar: BluetoothRemoteGATTCharacteristic,
        writeChar: BluetoothRemoteGATTCharacteristic
    ) => {
        const imageData = await captureFingerprint(responseChar, writeChar, 3);
        if (imageData) {
            saveFingerprint(imageData, 3);
          //  setCaptureStep(2);
            setStatus("✅  Huella 1 Capturada");
        }
    };

    // Compare fingerprints
    const compareFingerprints = async (
        responseChar: BluetoothRemoteGATTCharacteristic,
        writeChar: BluetoothRemoteGATTCharacteristic
    ) => {
        try {
            const matched = await matchFingerprints(writeChar, responseChar);
            setStatus(matched ? "🟢 Huellas coinciden" : "🔴 Huellas no coinciden");
        } catch (error) {
            console.error("❌ Error comparando huellas:", error);
            setStatus("Error comparando huellas");
        }
    };

    const matchFingerprints = async (
        writeChar: BluetoothRemoteGATTCharacteristic,
        responseChar: BluetoothRemoteGATTCharacteristic
    ): Promise<boolean> => {
        const matchCmd = buildMatchCommand();
        const response = await sendCommandAndGetResponse(writeChar, responseChar, matchCmd);

        if (!response || response.length < 8) {
            console.warn("⚠️ Respuesta de MATCH inválida o incompleta:", response);
            return false;
        }

        const status = response[7];

        if (status === 0x01) {
            console.log("✅ Las huellas coinciden");
            return true;
        } else {
            console.log("❌ Las huellas NO coinciden, response:", response);
            return false;
        }
    };

    // Main method, use to guide
    const captureFingerprintinitial = async (responseCharacteristic : BluetoothRemoteGATTCharacteristic, writeCharacteristic : BluetoothRemoteGATTCharacteristic) => {
        if (!device) return alert("No hay dispositivo 'Lector de Huella' conectado");

        setStatus("🟢 Coloca el dedo para la primera captura...");

        try {
            const responseChar = responseCharacteristic;
            const writeChar = writeCharacteristic;

            if (!responseChar || !writeChar) {
                console.error("No se pudo obtener las características de escritura y respuesta");
                return;
            }

            console.log("📬 Propiedades de writeChar:", writeChar.properties);
            console.log("📬 Propiedades de responseChar:", responseChar.properties);

            const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

            setStatus("📸 Capturando primera huella...");

            // CMD_ENROLHOST = 0x07;
            const cmdEnrole = buildSimpleCommand(0x07);
            await sendCommandAndGetResponse(writeChar, responseChar, cmdEnrole);
            await delay(300);

            // CMD_CAPTUREHOST = 0x08;
            const cmdCapture = buildSimpleCommand(0x08);

            await sendCommandAndGetResponse(writeChar, responseChar, cmdCapture);
            await delay(300);

            // Verifica si el template quedó cargado en buffer 1
            await checkTemplateInBuffer(0x01, writeChar, responseChar);
            await delay(300);

            setStatus("☝️ Retira el dedo y espera instrucciones...");

            await delay(400);

            setStatus("🟢 Vuelve a colocar el dedo para la segunda captura...");

            await delay(400);

            // CMD_ENROLHOST = 0x07;
            const cmdEnrole2 = buildSimpleCommand(0x07); // ENROLHOST otra vez (prepara el buffer)
            await sendCommandAndGetResponse(writeChar, responseChar, cmdEnrole2);
            await delay(300);

            // CMD_CAPTUREHOST = 0x08;
            const cmdCapture2 = buildSimpleCommand(0x08); // CAPTUREHOST para segunda huella
            await sendCommandAndGetResponse(writeChar, responseChar, cmdCapture2);
            await delay(300);

            // Verifica si el template quedó cargado en buffer 2
            await checkTemplateInBuffer(0x02, writeChar, responseChar);
            await delay(300);

            setStatus("🔍 Comparando huellas...");

            const matched = await matchFingerprints(writeChar, responseChar);

            if (matched) {
                console.log("🟢 Huellas coinciden");
            } else {
                console.log("🔴 Huellas no coinciden");
            }

            await delay(300);

            setStatus("🖼️ Capturando imagen final...");

            // 1. Comando GenImg (captura imagen) CMD_GETIMAGE = 0x50;
            const cmdGenImg = buildGenImgCommand();
            console.log("✅ Comando GenImg creado:", cmdGenImg);

            await new Promise(r => setTimeout(r, 300));

            const respGenImg  = await sendCommandAndGetResponseMultiPacket(
                writeChar,
                responseChar,
                cmdGenImg,        // Aquí va el comando que inicia la transferencia de imagen (ej. CMD_GETIMAGE2)
                36864,                     // Tamaño total esperado  36864 (ej. imagen RAW)
                3000                      // Tiempo de espera entre fragmentos (en milisegundos)
            );
            console.log("✅ Respuesta de GenImg recibida:", respGenImg);
            console.log("Bytes totales recibidos:", respGenImg.length);

            const statusGenImg = respGenImg[7];
            console.log("🔍 Respuesta completa:", respGenImg);
            console.log("📦 CMD recibido:", respGenImg[4]);
            console.log("📦 Tamaño de datos:", respGenImg[5], respGenImg[6]);
            console.log("📦 Status en respGenImg[7]:", respGenImg[7]);

            if (respGenImg.length < 20) {
                const status = respGenImg[7]; // solo si es una respuesta de control corta
                if (status !== 0x00) {
                    console.error("❌ Error al capturar imagen, código:", status);
                    return;
                }
                console.log("✅ Imagen capturada correctamente (paquete corto)");
            } else {
                // Aquí ↓↓↓ después de guardar la imagen
               //   const cmdGetCharBuffer1 = buildGetCharCommand(0x01);
               //   console.log("📤 Enviando CMD_GETCHAR (Buffer 1):", cmdGetCharBuffer1);
               // const respGetChar = await sendCommandAndGetResponse(writeChar, responseChar, cmdGetCharBuffer1);
              //  console.log("📥 Respuesta de GETCHAR:", respGetChar);
                console.log("📸 Imagen recibida completamente (bloques):", respGenImg.length, "bytes");

                const totalBytes = respGenImg.length;

                const inicio = respGenImg.slice(0, 36864);
                const final = respGenImg.slice(totalBytes - 36864);

            // Mostrar los primeros 10 bytes para comparar (puedes comparar más si deseas)
                console.log("🔎 Primeros 10 bytes (inicio):", Array.from(inicio.slice(0, 10)));
                console.log("🔎 Primeros 10 bytes (final):", Array.from(final.slice(0, 10)));

                const fullWidth = 256;
                const realWidth = 128; // mitad
                const height = 144; // 144
                const width = 256; // 128

                const imageRaw = respGenImg.slice(0, width * height);

                const croppedImage = new Uint8Array(realWidth * height);

                for (let y = 0; y < height; y++) {
                    for (let x = 0; x < realWidth; x++) {
                        // izquierda
                        croppedImage[y * realWidth + x] = respGenImg[y * fullWidth + x];

                        // o si quieres la derecha:
                        // croppedImage[y * realWidth + x] = rawImageBytes[y * fullWidth + (x + realWidth)];
                    }
                }

                // Guardar imagen como .raw
                // ver en el editor con 128 x 144 a 8 bits
              // const blob = new Blob([croppedImage], { type: "application/octet-stream" });
                const blob = new Blob([imageRaw], { type: "application/octet-stream" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = "fingerprintNEW.raw";
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                console.log("✅ Imagen .raw descargada");



                const canvas = document.createElement("canvas");
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext("2d");

                if (!ctx) {
                    console.error("❌ Error: no se pudo obtener el contexto 2D del canvas.");
                    return;
                }

                const imageData = ctx.createImageData(width, height);
                const data = imageData.data;

                // Copiar el buffer de escala de grises en formato RGBA
                for (let i = 0; i < imageRaw.length; i++) {
                    const gray = imageRaw[i];
                    data[i * 4] = gray;     // R
                    data[i * 4 + 1] = gray; // G
                    data[i * 4 + 2] = gray; // B
                    data[i * 4 + 3] = 255;  // A (opaco)
                }

                ctx.putImageData(imageData, 0, 0);

                // Exportar como base64 (PNG codificado)
                const dataUrl = canvas.toDataURL("image/png");
                setBase64Png(dataUrl);
                localStorage.setItem("fingerprintbase64", dataUrl);

                // Sin encabezado, por si se necesita guardarlo en DB
                const base64Only = dataUrl.split(",")[1];
                localStorage.setItem("fingerprintbase64sinencabezado", base64Only);

                console.log("✅ Imagen en Base64 guardada en localStorage");

                // Exportar como PNG
                canvas.toBlob(blob => {
                        if (blob) {
                        const urlPng = URL.createObjectURL(blob);
                        const aPng = document.createElement("a");
                        aPng.href = urlPng;
                        aPng.download = "fingerprint_clean.png";
                        console.log("✅ Imagen en PNG descargada");
                        document.body.appendChild(aPng);
                        aPng.click();
                        document.body.removeChild(aPng);
                    } else {
                        console.error("❌ No se pudo crear el blob PNG.");
                    }
                }, "image/png");
            }

        } catch (error) {
            console.error("❌ Error:", error);
        }
    };

    const captureFingerPrintWithCropped = async (responseCharacteristic : BluetoothRemoteGATTCharacteristic, writeCharacteristic : BluetoothRemoteGATTCharacteristic) => {
        if (!device) return alert("No hay dispositivo conectado.");

        try {
            const server = await device.gatt?.connect() as BluetoothRemoteGATTServer | undefined;
            const service = await server?.getPrimaryService("0000fff0-0000-1000-8000-00805f9b34fb");
            const responseChar = await service?.getCharacteristic("0000fff1-0000-1000-8000-00805f9b34fb");
            const writeChar = await service?.getCharacteristic("0000fff2-0000-1000-8000-00805f9b34fb");

            if (!responseChar || !writeChar) {
                console.error("No se pudo obtener la característica de escritura");
                return;
            }

            console.log("📬 Propiedades de writeChar:", writeChar.properties);
            console.log("📬 Propiedades de responseChar:", responseChar.properties);

            // 1. Comando GenImg (captura imagen)
            const cmdGenImg = buildGenImgCommand();
            console.log("✅ Comando GenImg creado:", cmdGenImg);

            const respGenImg  = await sendCommandAndGetResponseMultiPacket(
                writeChar,
                responseChar,
                cmdGenImg,
                36864,
                3000
            );

            if (respGenImg.length < 20) {
                const status = respGenImg[7]; // Only if it's a short control response
                if (status !== 0x00) {
                    console.error("❌ Error al capturar imagen, código:", status);
                    return;
                }
                console.log("✅ Imagen capturada correctamente (paquete corto)");
            } else {
                const cmdGetCharBuffer1 = buildGetCharCommand(0x02);
                console.log("📤 Enviando CMD_GETCHAR (Buffer 1):", cmdGetCharBuffer1);
                const respGetChar = await sendCommandAndGetResponse(writeChar, responseChar, cmdGetCharBuffer1);
                console.log("📥 Respuesta de GETCHAR:", respGetChar);
                console.log("📸 Imagen recibida completamente (bloques):", respGenImg.length, "bytes");

                const fullWidth = 256;
                const realWidth = 128;
                const height = 144;
                const width = 128;

                const croppedImage = new Uint8Array(realWidth * height);

                for (let y = 0; y < height; y++) {
                    for (let x = 0; x < realWidth; x++) {
                        // Cropped to the left side
                        // croppedImage[y * realWidth + x] = respGenImg[y * fullWidth + x];

                        // Cropped to the right side
                        croppedImage[y * realWidth + x] = respGenImg[y * fullWidth + (x + realWidth)];
                    }
                }

                // Save RAW image file
                // View in editor with 128 x 144 at 8 bits
                const blob = new Blob([croppedImage], { type: "application/octet-stream" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = "fingerprintNEW2.raw";
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                console.log("✅ Imagen .raw descargada");

                const canvas = document.createElement("canvas");
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext("2d");

                if (!ctx) {
                    console.error("❌ Error: no se pudo obtener el contexto 2D del canvas.");
                    return;
                }

                const imageData = ctx.createImageData(width, height);
                const data = imageData.data;

                // Copy the grayscale buffer in RGBA format
                for (let i = 0; i < croppedImage.length; i++) {
                    const gray = croppedImage[i];
                    data[i * 4] = gray;     // R
                    data[i * 4 + 1] = gray; // G
                    data[i * 4 + 2] = gray; // B
                    data[i * 4 + 3] = 255;  // A (opaco)
                }

                ctx.putImageData(imageData, 0, 0);

                const dataUrl = canvas.toDataURL("image/png");
                setBase64Png(dataUrl);
                localStorage.setItem("fingerprintbase64-2", dataUrl);

                const base64Only = dataUrl.split(",")[1];
                localStorage.setItem("fingerprintbase64-2sinencabezado", base64Only);

                // Export as PNG
                canvas.toBlob(blob => {
                    if (blob) {
                        const urlPng = URL.createObjectURL(blob);
                        const aPng = document.createElement("a");
                        aPng.href = urlPng;
                        aPng.download = "fingerprint_clean2.png";
                        console.log("✅ Imagen en PNG #2 descargada");
                        document.body.appendChild(aPng);
                        aPng.click();
                        document.body.removeChild(aPng);
                    } else {
                        console.error("❌ No se pudo crear el blob PNG.");
                    }
                }, "image/png");
            }

        } catch (error) {
            console.error("❌ Error:", error);
        }
    };

    const getDeviceInfo = async (responseCharacteristic : BluetoothRemoteGATTCharacteristic, writeCharacteristic : BluetoothRemoteGATTCharacteristic) => {
        if (!device) {
            alert("No hay dispositivo conectado.");
            return;
        }

        try {
            const writeChar = writeCharacteristic;
            const responseChar = responseCharacteristic;

            if (!writeChar || !responseChar) {
                throw new Error("No se pudieron obtener las características del dispositivo.");
            }

            // CMD_GETSN = 0x10
            const snCmd = buildSimpleCommand(0x10);
            const snResp = await sendCommandAndGetResponse(writeChar, responseChar, snCmd);
            const sn = new TextDecoder().decode(snResp.slice(8)); // Asumiendo que empieza desde el byte 8
            console.log("📟 Número de serie del dispositivo (SN):", sn);

            // CMD_GETBAT = 0x21
            const batCmd = buildSimpleCommand(0x21);
            const batResp = await sendCommandAndGetResponse(writeChar, responseChar, batCmd);
            const batteryLevel = batResp[8]; // Byte 8 suele contener el porcentaje
            console.log("🔋 Nivel de batería:", batteryLevel + "%");

            // CMD_GET_VERSION = 0x22
            const verCmd = buildSimpleCommand(0x22);
            const verResp = await sendCommandAndGetResponse(writeChar, responseChar, verCmd);
            const version = new TextDecoder().decode(verResp.slice(8));
            console.log("🛠️ Versión del firmware:", version);

        } catch (error) {
            console.error("❌ Error al obtener la información del dispositivo:", error);
        }
    };

    // Method to check if a template is in the buffer
    const checkTemplateInBuffer = async (
        bufferId: number,
        writeChar: BluetoothRemoteGATTCharacteristic,
        responseChar: BluetoothRemoteGATTCharacteristic
    ): Promise<boolean> => {
        try {
            const cmd = buildGetCharCommand(bufferId);
            console.log(`📤 Enviando CMD_GETCHAR para buffer ${bufferId}:`, cmd);

            const response = await sendCommandAndGetResponse(writeChar, responseChar, cmd);
            console.log("📥 Respuesta de GETCHAR:", response);

            const status = response[7]; // Byte de estado

            if (status === 0x00) {
                console.log(`✅ El buffer ${bufferId} contiene un template válido.`);
                return true;
            } else if (status === 0x06) {
                console.warn(`⚠️ El buffer ${bufferId} está vacío o no contiene template.`);
                return false;
            } else {
                console.warn(`⚠️ Estado inesperado (${status}) en buffer ${bufferId}.`);
                return false;
            }

        } catch (error) {
            console.error("❌ Error al verificar template en buffer:", error);
            return false;
        }
    };

    // Method to compare two fingerprints using the backend
    const compareFingersTemplateByBackend = async (
        finger1: string | null,
        finger2: string | null,
        token?: string // opcional, si usas auth
    ) => {
        try {
            if (!finger1 || !finger2) {
                throw new Error("Ambas huellas deben estar capturadas primero");
            }

            setStatus("🔍 Comparando huellas con el backend...");

            // Delete header if it exists
            const cleanFinger1 = finger1.startsWith('data:') ? finger1.split(',')[1] : finger1;
            const cleanFinger2 = finger2.startsWith('data:') ? finger2.split(',')[1] : finger2;

            // Identify if we are in a local environment or production
            const isLocal = window.location.hostname === "localhost";

            // Build the endpoint URL using local microservice or production microservice
            const baseUrl = isLocal
                ? "http://localhost:5000" // The local development server, microservice Java - SourceAFIS fingerprint matcher
                :  `${process.env.REACT_APP_API_URL}`; // prod

            // Build the endpoint URL for production
            const baseUrlProd = `${process.env.REACT_APP_API_URL}`; // prod

            const endpoint = `${baseUrl}/compare`;

            // Token for matcher Java SourceAfis service
            const matcherToken = process.env.REACT_APP_MATCHER_TOKEN;

            // Preparar headers
            const headers: Record<string, string> = {
                "Content-Type": "application/json",
                "Accept": "application/json"
            };

            if (matcherToken) {
                headers["x-access"] = matcherToken;
            }

            const response = await fetch(endpoint, {
                method: "POST",
                headers,
                body: JSON.stringify({
                    probe: cleanFinger1,
                    candidate: cleanFinger2
                }),
            });

            if (!response.ok) {
                throw new Error(`Error del servidor: ${response.status}`);
            }

            const result = await response.json();

            console.log("Resultado de comparación:", result);
            setStatus(result.match ? "✅ Huellas coinciden" : "❌ Huellas no coinciden");
            setStatus(prev => `${prev} (Score: ${result.score?.toFixed(2)})`);

            return result;

        } catch (error) {
            console.error("❌ Error comparando huellas:", error);
            setStatus(`Error: ${error instanceof Error ? error.message : String(error)}`);
            return { error: error instanceof Error ? error.message : String(error) };
        }
    };

    const compareFingersTemplateByBackend02 = async (
        finger1: string | null,
        finger2: string | null,
        finger3: string | null,
        token?: string
    ) => {
        try {
            if (!finger1 || !finger2 || !finger3) {
                throw new Error("Las tres huellas deben estar capturadas");
            }

            setStatus("🔍 Comparando huellas (1 vs 3)...");

            const clean = (finger: string) =>
                finger.startsWith("data:") ? finger.split(",")[1] : finger;

            const cleanFinger1 = clean(finger1);
            const cleanFinger2 = clean(finger2);
            const cleanFinger3 = clean(finger3);

            const isLocal = window.location.hostname === "localhost";

            const baseUrl = isLocal
                ? "http://localhost:5000"
                : `${process.env.REACT_APP_API_URL}`;

            const endpoint = `${baseUrl}/compare`;
            const matcherToken = process.env.REACT_APP_MATCHER_TOKEN;

            const headers: Record<string, string> = {
                "Content-Type": "application/json",
                "Accept": "application/json"
            };

            if (matcherToken) {
                headers["x-access"] = matcherToken;
            }

            // Primer intento: finger1 vs finger3
            const response1 = await fetch(endpoint, {
                method: "POST",
                headers,
                body: JSON.stringify({
                    probe: cleanFinger1,
                    candidate: cleanFinger3
                }),
            });

            if (!response1.ok) {
                throw new Error(`Error del servidor (1 vs 3): ${response1.status}`);
            }

            const result1 = await response1.json();
            console.log("Resultado 1 vs 3:", result1);

            if (result1.match) {
                setStatus(`✅ Match entre 1 y 3 (Score: ${result1.score.toFixed(2)})`);
                return result1;
            }

            // Segundo intento: finger2 vs finger3
            setStatus("🔍 Reintentando: comparando huellas (2 vs 3)...");

            const response2 = await fetch(endpoint, {
                method: "POST",
                headers,
                body: JSON.stringify({
                    probe: cleanFinger2,
                    candidate: cleanFinger3
                }),
            });

            if (!response2.ok) {
                throw new Error(`Error del servidor (2 vs 3): ${response2.status}`);
            }

            const result2 = await response2.json();
            console.log("Resultado 2 vs 3:", result2);

            setStatus(result2.match
                ? `✅ Match entre 2 y 3 (Score: ${result2.score.toFixed(2)})`
                : `❌ No hay coincidencia en ninguno (Score: ${result2.score.toFixed(2)})`
            );

            return result2;

        } catch (error) {
            console.error("❌ Error comparando huellas:", error);
            setStatus(`Error: ${error instanceof Error ? error.message : String(error)}`);
            return { error: error instanceof Error ? error.message : String(error) };
        }
    };


    const handleClick01 = () => {
        navigate('/seller_profile');
    };
    return (
        <div className="fingerprint-container">
            <h1 className="fingerprint-title">VERIFICAR VENTERO POR HUELLA DACTILAR</h1>
            <div className="status-container">
                <span className="status-label">Estado Del Dispositivo:</span>
                <br/>
                <span className={"status-value"}>{status}</span>
            </div>
            <div className="buttons-finger-container">
                <button
                    onClick={isConnected ? disconnectFromDevice : connectToDevice}
                    className="action-button"
                    style={{
                        backgroundColor: isConnected ? '#4CAF50' : '#56575a'
                    }}
                >
                    <i className={isConnected ? "fa-regular fa-circle-stop" : "fa-regular fa-circle-play"}></i>
                    {isConnected ? 'Desconectar lector de huella' : 'Conectarse al lector de huella'}
                </button>
                <button
                    onClick={() => {
                        if (!responseCharacteristic || !writeCharacteristic) {
                            alert("Las características Bluetooth no están inicializadas");
                            return;
                        }
                        captureStep === 1
                            ? captureFingerprint01(responseCharacteristic, writeCharacteristic)
                            : captureFingerprint02(responseCharacteristic, writeCharacteristic);
                    }}
                    disabled={isCapturing || !responseCharacteristic || !writeCharacteristic}
                    className="action-button">
                    <i className={"fa-regular fa-hand-point-up"}></i>
                    {isCapturing ? "Capturando..." : `Capturar huella dactilar #${captureStep}`}
                </button>
                <button
                    onClick={() => {
                        if (!responseCharacteristic || !writeCharacteristic) {
                            alert("No se han inicializado la conexión Bluetooth");
                            return;
                        }
                        captureFingerprint03(responseCharacteristic, writeCharacteristic);
                    }}
                    className="action-button">
                    Capturar Huella - Simulando backend
                </button>
                <button
                    onClick={() => {
                        if (!finger1 || !finger2) {
                            alert("Ambas huellas deben estar capturadas primero");
                            return;
                        }
                        compareFingersTemplateByBackend(finger1, finger2);
                    }}
                    disabled={!finger1 || !finger2}
                    className="action-button">
                    <i className={"fa-regular fa-circle-check"}></i>
                    Verificar huella con SISDEP
                </button>
                {/*<button
                    onClick={() => {
                        if (!finger1 || !finger2) {
                            alert("Ambas huellas deben estar capturadas primero");
                            return;
                        }
                        compareFingersTemplateByBackend02(finger1, finger2, finger3);
                    }}
                    disabled={!finger1 || !finger2}
                    className="action-button">
                    <i className={"fa-regular fa-circle-check"}></i>
                    Verificar huella con SISDEP 3
                </button> */}
                <button  onClick={() => {
                    if (!responseCharacteristic || !writeCharacteristic) {
                        alert("No se han inicializado la conexión Bluetooth");
                        return;
                    }
                    getDeviceInfo(responseCharacteristic, writeCharacteristic);
                }} className="action-button">
                    <i className={"fa-regular fa-rectangle-list"}></i>
                    Información del lector de huella
                </button>
                <div onClick={handleClick01}>
                    <button className="action-button">
                        <i className={"fa-regular fa-user"}></i>
                        Regresar al perfil del Ventero
                </button>
                </div>
                {base64Png && (
                    <div>
                        <h2 className="fingerprint-template">HUELLA CAPTURADA POR EL DISPOSITIVO</h2>
                        <img src={base64Png} alt="Huella digital" />
                    </div>
                )}
            </div>
        </div>
    );
};

export default FingerprintScanner;

